<template>
    <SelectRoot
        v-model="model"
        @update:model-value="emits('change')">
        <SelectTrigger
            v-bind="$attrs"
            aria-label="Customise options"
            :disabled
            :class="[
                { 'flex-row-reverse': iconPosition === 'left' },
                disabled
                    ? 'relative border-ash-300 text-white/50'
                    : 'border-ash-45 text-white data-[state=open]:text-white',
                size === 'large'
                    ? 'min-h-16 py-5 text-20/120'
                    : 'min-h-[2.75rem] py-2 text-16/148',
            ]"
            class="inline-flex min-w-[12rem] select-none items-center justify-between gap-5 rounded-8 border px-3 font-semibold outline-none transition-colors duration-300">
            <SelectValue class="w-full truncate text-left">
                {{ activeOptionText || placeholder }}
            </SelectValue>
            <AtomIcon
                name="chevron-down"
                :class="disabled ? 'text-ash-300' : 'text-white'"
                class="size-5" />
        </SelectTrigger>

        <SelectPortal>
            <SelectContent
                position="popper"
                :body-lock="false"
                :side-offset="10"
                class="z-[50] min-w-[12rem] rounded-12 bg-ash-200 shadow-sm transition-all duration-300 hover:shadow-ash data-[state=closed]:data-[side=bottom]:animate-slideDownAndFade data-[state=open]:data-[side=bottom]:animate-slideUpAndFade">
                <SelectViewport class="px-2 py-3">
                    <SelectItem
                        v-for="option in options"
                        :key="option.value"
                        :value="option.value"
                        class="peer relative flex select-none items-center rounded-[3px] px-8 py-1 text-14/148 text-ash transition-all duration-300 data-[disabled]:pointer-events-none data-[highlighted]:bg-accent-300/50 data-[highlighted]:text-accent data-[highlighted]:outline-none">
                        <SelectItemIndicator
                            class="absolute left-0 inline-flex h-full items-center justify-center p-1 text-accent transition-all duration-300">
                            <AtomIcon
                                name="success"
                                class="size-5" />
                        </SelectItemIndicator>
                        <SelectItemText>
                            {{ option.text }}
                        </SelectItemText>
                    </SelectItem>
                </SelectViewport>
            </SelectContent>
        </SelectPortal>
    </SelectRoot>
</template>

<script setup lang="ts">
import type { IDropdownOption } from '~/components/atom/atom-dropdown/atom-dropdown.types'
import AtomIcon from '~/components/atom/atom-icon/AtomIcon.vue'

const model = defineModel({
    type: [String, Number, null] as PropType<string | number | null>,
    default: null,
})

const emits = defineEmits(['change'])

const props = defineProps({
    placeholder: {
        type: String as PropType<string>,
        default: '',
    },
    iconPosition: {
        type: String as PropType<'left' | 'right'>,
        default: 'right',
    },
    size: {
        type: String as PropType<'medium' | 'large'>,
        default: 'medium',
    },
    appearance: {
        type: String as PropType<'accent' | 'outline'>,
        default: 'outline',
    },
    options: {
        type: Array as PropType<IDropdownOption[]>,
        default: () => [],
    },
    disabled: {
        type: Boolean as PropType<boolean>,
        default: false,
    },
})

const activeOptionText: ComputedRef<string | number | null> = computed(() => {
    return (
        props.options.find(
            (option: IDropdownOption) => option.value === model.value
        )?.text || null
    )
})
</script>
